import { css } from "@emotion/css";
import { ChevronDownIcon, ChevronUpIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { Button } from "../../Button/Button";
import { useDropdownButton } from "../../Dropdown";
import { FormDescription } from "../Primitives/FormDescription";
import { Input } from "../Primitives/Input";
import { InputError } from "../Primitives/InputError";
import InputLabel from "../Primitives/InputLabel";
import { Listbox } from "../Primitives/Listbox";
import { type TextFieldProps } from "../TextField/TextField";
interface TimeOption {
    label: string;
    value: string;
}
export interface TimePickerProps extends Omit<TextFieldProps<string>, "type" | "suffix"> {
    /**
     * Time increment options to show in dropdown
     */
    increment?: "hourly" | "half-hourly";
}
export function TimePicker({ increment = "hourly", value, onChange, label, placeholder, description, error, disabled = false, hasRequiredMarker = false, hasOptionalMarker = false, hasDefaultMarker = false, popover, autoFocus = false, readOnly = false, name, prefix, }: TimePickerProps) {
    const { isOpen, closeDropdown, setButtonRef, toggleDropdown, setDropdownRef, dropdownAttributes, buttonAriaAttributes } = useDropdownButton({ trapFocus: false, dropdownAriaRole: "listbox" });
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const baseId = React.useId();
    const inputId = `timepicker-${name ? name + "-" : ""}${baseId}`;
    const getOptionId = (index: number) => `${dropdownAttributes.dropdownId}-option-${index}`;
    const options = React.useMemo(() => {
        if (increment === "half-hourly") {
            return generateTimeOptions(30);
        }
        return generateTimeOptions(60);
    }, [increment]);
    const handleInputChange = (newValue: string) => {
        onChange(newValue);
    };
    const handleOptionClick = (option: TimeOption) => {
        onChange(option.value);
        closeDropdown({ moveFocusToButton: true });
    };
    const handleButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        toggleDropdown();
    };
    const handleTabPressed = (event: React.KeyboardEvent) => {
        event.preventDefault();
        closeDropdown({ moveFocusToButton: true });
    };
    const dropdownButton = (<Button ref={setButtonRef} icon={isOpen ? <ChevronUpIcon size={20}/> : <ChevronDownIcon size={20}/>} onClick={handleButtonClick} importance="ghost" size="medium" accessibleName="Toggle time options" {...buttonAriaAttributes}/>);
    return (<div ref={anchorRef} className={containerStyles}>
            <div>
                <InputLabel label={label} htmlFor={inputId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${inputId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <div>
                <Input id={inputId} type="time" value={value} onChange={handleInputChange} placeholder={placeholder} disabled={disabled} required={hasRequiredMarker} readOnly={readOnly} autoFocus={autoFocus} name={name} prefix={prefix} suffix={dropdownButton} aria-invalid={!!error} aria-describedby={[description ? `${inputId}-description` : null, error ? `${inputId}-error` : null].filter(Boolean).join(" ") || undefined}/>
                {isOpen && (<div className={dropdownContainerStyles}>
                        <Listbox ref={setDropdownRef} id={dropdownAttributes.dropdownId} value={value} options={options} onOptionSelected={handleOptionClick} getOptionId={getOptionId} onTabPressed={handleTabPressed} accessibleName="Select a time"/>
                    </div>)}
            </div>

            {error && <InputError error={error} id={`${inputId}-error`}/>}
        </div>);
}
const generateTimeOptions = (intervalMinutes: number): TimeOption[] => {
    const options: TimeOption[] = [];
    const totalMinutesInDay = 24 * 60; // 1440 minutes
    for (let minutes = 0; minutes < totalMinutesInDay; minutes += intervalMinutes) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const hourStr = hours.toString().padStart(2, "0");
        const minStr = mins.toString().padStart(2, "0");
        const value = `${hourStr}:${minStr}`;
        // Convert to 12-hour format for display
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours < 12 ? "AM" : "PM";
        const label = `${displayHours}:${minStr} ${ampm}`;
        options.push({ label, value });
    }
    return options;
};
const containerStyles = css({
    position: "relative",
    width: "12.5rem",
    display: "flex",
    flexDirection: "column",
    gap: space[8],
    // Hide native calendar/clock icons in all browsers
    "input[type='time']": {
        justifyContent: "flex-start",
        "&::-webkit-calendar-picker-indicator": {
            display: "none",
        },
    },
    "& *": {
        boxSizing: "border-box",
    },
});
const dropdownContainerStyles = css({
    position: "absolute",
    marginTop: space[8],
    zIndex: 1,
    width: "100%",
});
